<script>
// A ancoragem (resolver o alvo, medir o rect, retry, listeners de viewport e
// portalização para o body) vive no mixin, compartilhada com o tour guiado da
// Jornada. Este componente segue dono da APRESENTAÇÃO — spotlight, card e o
// modo slot legado.
import { anchoredTargetMixin } from '@modules/global/mixins/anchored-target-mixin'

export default {
  name: 'Tour',
  mixins: [anchoredTargetMixin],
  props: {
    stepsTour: {
      type: Array,
      default: () => [],
    },
    currentStep: {
      type: Number,
      default: 0,
    },

    disable: {
      type: Boolean,
      default: false,
    },

    // MODO ANCORADO (opcional, aditivo). Quando vazio, o Tour opera no modo slot
    // legado (envolve `<slot name="content">` + box-shadow) — comportamento
    // intacto para os usos do Settings. Quando presente, o Tour localiza o alvo
    // via document.querySelector (funciona inclusive para DOM teleportado),
    // desenha o spotlight sobre o rect do alvo e posiciona o card relativo a ele.
    targetSelector: {
      type: String,
      default: '',
    },
  },

  computed: {
    anchored() {
      return !!this.targetSelector
    },

    // Contrato do anchoredTargetMixin.
    anchorSelector() {
      return this.targetSelector
    },

    anchorActive() {
      return this.anchored && !this.disable
    },

    shadow() {
      return this.disable ? '' : ' 0px 0px 0px 3000px rgba(0,0,0,0.39)'
    },

    rootStyle() {
      if (this.anchored) {
        return { 'z-index': this.disable ? 0 : 5 }
      }
      return { 'box-shadow': this.shadow, 'z-index': this.disable ? 0 : 5 }
    },

    // Spotlight = "buraco" claro sobre o alvo, com box-shadow gigante escurecendo
    // o resto (mesma técnica do modo legado, mas posicionado no rect do alvo).
    spotlightStyle() {
      const rect = this.targetRect
      if (!rect) return {}
      const pad = 6
      return {
        position: 'fixed',
        top: `${rect.top - pad}px`,
        left: `${rect.left - pad}px`,
        width: `${rect.width + pad * 2}px`,
        height: `${rect.height + pad * 2}px`,
        'box-shadow': '0px 0px 0px 3000px rgba(0,0,0,0.39)',
        'border-radius': '8px',
        'pointer-events': 'none',
        'z-index': 5,
        transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
      }
    },

    // Posição do card relativo ao alvo: abaixo por padrão; acima se faltar espaço.
    // Sem alvo (targetRect null) → centralizado, para o usuário nunca ficar preso.
    cardStyle() {
      if (!this.anchored) return null

      const base = { position: 'fixed', 'z-index': 6, 'pointer-events': 'auto' }

      if (!this.targetRect) {
        return {
          ...base,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }
      }

      const pos = this.computeAnchoredCardPosition(this.targetRect)
      return { ...base, top: `${pos.top}px`, left: `${pos.left}px` }
    },
  },

  watch: {
    currentStep() {
      if (this.anchorActive) {
        this.scheduleRectUpdate()
      }
    },
  },

  methods: {
    // Clampa a posição do card dentro da viewport, abaixo do alvo por padrão e
    // acima quando não há espaço. Extraído do computed para manter cada função
    // curta (SRP + limite de statements do lint).
    computeAnchoredCardPosition(rect) {
      const cardWidth = 260
      const cardHeight = 180
      const gap = 12

      let top = rect.bottom + gap
      if (top + cardHeight > window.innerHeight) {
        top = rect.top - cardHeight - gap
      }
      if (top < 8) top = 8

      let left = rect.left
      const maxLeft = window.innerWidth - cardWidth - 8
      if (left > maxLeft) left = maxLeft
      if (left < 8) left = 8

      return { top, left }
    },
  },
}
</script>

<template>
  <div class="tour" :class="{ 'tour--anchored': anchored }" :style="rootStyle">
    <div
      v-if="anchored && !disable && targetRect"
      class="tour-spotlight"
      :style="spotlightStyle"
    />

    <div
      v-if="!disable"
      class="tour-info"
      :class="{ 'tour-info--anchored': anchored }"
      :style="anchored ? cardStyle : null"
    >
      <div class="d-flex justify-content-end">
        <v-btn
          icon
          style="background-color: white; width: 16px; height: 16px"
          aria-label="Fechar tour"
          @click="$emit('closeTour')"
        >
          <icon-svg name="close" size="6" />
        </v-btn>
      </div>

      <p class="text-white text-sm font-medium mt-2 px-2">
        {{ stepsTour[currentStep] }}
      </p>

      <div class="tour-steps-points">
        <div
          v-for="(item, index) in stepsTour"
          :key="index"
          class="dot"
          :class="{ 'bg-primary': currentStep === index }"
        ></div>
      </div>
      <div v-if="currentStep !== stepsTour.length - 1" class="tour-bottom">
        <BaseButton
          style="background-color: white; color: var(--primary)"
          minWidth="80px"
          @click="$emit('closeTour')"
        >
          Pular o tour
        </BaseButton>
        <BaseButton primary minWidth="80px" @click="$emit('nextStep')">
          Próximo
        </BaseButton>
      </div>
      <div v-else class="tour-bottom">
        <BaseButton primary style="width: 100%" @click="$emit('nextStep')">
          Concluir
        </BaseButton>
      </div>
    </div>

    <div v-if="!disable && !anchored" class="arc" />

    <slot name="content" />
  </div>
</template>

<style lang="sass" scoped>
.tour
    width: auto
    position: relative
    border-radius: 8px

    &.tour--anchored
      position: fixed
      top: 0
      left: 0
      width: 100vw
      height: 100vh
      pointer-events: none
      border-radius: 0

    .arc
      position: absolute
      top: -20px
      left: 86%
      height: 0
      width: 0
      border: 10px solid transparent
      border-bottom-color: #fff

    .tour-info
      position: absolute
      width: 260px
      background: rgba(78, 81, 97, 0.5)
      border-radius: 14px
      padding: 8px
      top: -190px
      right: 0

      &.tour-info--anchored
        right: auto

    .tour-bottom
      display: flex
      justify-content: center
      margin-bottom: 8px
      margin-top: 16px
      button
        width: 97px !important
        min-width: 97px !important
        height: 24px
        font-size: 12px
        padding: 4px

    .tour-steps-points
      display: flex
      justify-content: center
      gap: 6px
      .dot
        height: 6px
        width: 6px
        border-radius: 100%
        background: white
</style>
